import React, { useEffect, useState } from 'react';
import { Upload, message, Image } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getToken } from '../utils/request';

interface ImageUploadProps {
  // 单图模式为 string，多图模式为 string[]
  value?: string | string[];
  onChange?: (v: string | string[]) => void;
  multiple?: boolean;
  // 尺寸规范提示文案
  tip?: string;
}

let uidSeed = 0;

// 已有 URL 转成 antd fileList 条目（用于回显）
const toFile = (url: string): UploadFile => ({
  uid: `exist-${++uidSeed}`,
  name: url.split('/').pop() || 'image',
  status: 'done',
  url,
});

const extractUrl = (f: UploadFile): string | undefined =>
  (f.response as any)?.data?.url || f.url;

// 多图模式下的可拖拽列表项：仅负责把每个缩略图接入 dnd-kit 排序，实际换位由 onDragEnd 完成
const DraggableItem: React.FC<{ originNode: React.ReactElement; file: UploadFile }> = ({
  originNode,
  file,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.uid,
    disabled: file.status === 'uploading',
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 99, userSelect: 'none' as const } : {}),
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {originNode}
    </div>
  );
};

// 受控图片上传组件：走管理端上传接口，支持单图/多图与已有 URL 回显；多图支持拖拽调整顺序（第一张通常作主图/封面）
const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange, multiple = false, tip }) => {
  const urls: string[] = multiple
    ? (Array.isArray(value) ? value : []).filter(Boolean)
    : value
      ? [value as string]
      : [];

  const [fileList, setFileList] = useState<UploadFile[]>(() => urls.map(toFile));
  // 点击缩略图放大预览（受控）：antd picture-card 仅在提供 onPreview 时才会弹出大图
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  // 外部值变化（表单回填/重置）时重建列表，仅在完成态 URL 不一致时触发，避免打断上传中状态
  useEffect(() => {
    const doneUrls = fileList.filter((f) => f.status !== 'uploading').map((f) => extractUrl(f)).filter(Boolean);
    if (JSON.stringify(doneUrls) !== JSON.stringify(urls)) {
      setFileList(urls.map(toFile));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 由 fileList 计算完成态 URL 并抛给外部（多图抛数组，单图抛字符串）
  const commit = (fl: UploadFile[]) => {
    setFileList(fl);
    const done = fl
      .filter((f) => f.status === 'done')
      .map((f) => extractUrl(f))
      .filter(Boolean) as string[];
    onChange?.(multiple ? done : done[0] || '');
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: fl }) => {
    if (fl.some((f) => f.status === 'error')) {
      message.error((fl.find((f) => f.status === 'error')?.response as any)?.message || '上传失败，请重试');
    }
    commit(fl);
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      message.error('图片不能超过 5MB');
      return Upload.LIST_IGNORE;
    }
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      message.error('仅支持 jpg / png / webp 图片');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  // distance:1 让"点击删除按钮"不被误判为拖拽
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 1 } }));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const activeIndex = fileList.findIndex((i) => i.uid === active.id);
    const overIndex = fileList.findIndex((i) => i.uid === over.id);
    if (activeIndex < 0 || overIndex < 0) return;
    commit(arrayMove(fileList, activeIndex, overIndex));
  };

  const handlePreview: UploadProps['onPreview'] = (file) => {
    const url = extractUrl(file);
    if (url) {
      setPreviewSrc(url);
      setPreviewOpen(true);
    }
  };

  const uploadEl = (
    <Upload
      listType="picture-card"
      action={`${process.env.REACT_APP_API_BASE || '/api'}/upload/admin-image`}
      name="file"
      headers={{ Authorization: `Bearer ${getToken()}` }}
      accept="image/jpeg,image/png,image/webp"
      fileList={fileList}
      multiple={multiple}
      onChange={handleChange}
      beforeUpload={beforeUpload}
      onPreview={handlePreview}
      onRemove={() => true}
      itemRender={
        multiple
          ? (originNode, file) => <DraggableItem originNode={originNode as React.ReactElement} file={file} />
          : undefined
      }
    >
      {(!multiple || fileList.length < 9) && (
        <div>
          <PlusOutlined />
          <div style={{ marginTop: 4 }}>上传</div>
        </div>
      )}
    </Upload>
  );

  return (
    <div>
      {multiple ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={fileList.map((f) => f.uid)} strategy={verticalListSortingStrategy}>
            {uploadEl}
          </SortableContext>
        </DndContext>
      ) : (
        uploadEl
      )}
      {tip && (
        <div style={{ color: '#9ca3af', fontSize: 12, lineHeight: 1.6 }}>
          {multiple ? '拖动缩略图可调整顺序，第一张为主图/封面。' : ''}
          {tip}
        </div>
      )}
      {/* 隐藏的受控预览 Image：onPreview 命中后弹出大图，可缩放/旋转 */}
      {previewSrc && (
        <Image
          style={{ display: 'none' }}
          src={previewSrc}
          preview={{
            open: previewOpen,
            onOpenChange: (v) => setPreviewOpen(v),
          }}
        />
      )}
    </div>
  );
};

export default ImageUpload;
